import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchUsers, getNavigateMenu, getUserNavigateMenus, createUserNavigateMenu, deleteUserNavigateMenu, NavigationMenuItem, UserNavigationMenuItem, changeUserRol } from "@/services/authService";
import { getChannelsByUser } from "@/services/channelService";
import { Mail, Phone, User as UserIcon, Calendar, CheckCircle, XCircle, TrendingUp, ToggleLeft, ToggleRight, LayoutGrid, ArrowLeftRight, HelpCircle } from "lucide-react";
import { MessageSquare, Workflow, EuroIcon, MessageCircle, Smartphone } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import IAUsage from "@/components/channels/IAUsage";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const UserConfig = () => {
    const { t } = useTranslation();
    const { userId } = useParams<{ userId: string }>();
    const queryClient = useQueryClient();

    // Consultar todos los usuarios
    const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
        queryKey: ["users"],
        queryFn: fetchUsers,
    });

    // Consultar canales del usuario específico
    const { data: channels, isLoading: channelsLoading, error: channelsError } = useQuery({
        queryKey: ["channels", userId],
        queryFn: () => userId ? getChannelsByUser(userId) : Promise.resolve(undefined),
        enabled: !!userId,
    });

    // Permisos: menús globales
    const { data: navigationMenus, isLoading: menusLoading } = useQuery({
        queryKey: ["navigationMenus"],
        queryFn: getNavigateMenu,
    });

    // Permisos del usuario
    const { data: userMenus, isLoading: userMenusLoading } = useQuery({
        queryKey: ["userMenus", userId],
        queryFn: () => (userId ? getUserNavigateMenus(userId) : Promise.resolve([] as UserNavigationMenuItem[])),
        enabled: !!userId,
    });

    const addPermission = useMutation({
        mutationFn: (menuId: number) => createUserNavigateMenu({ userId: userId || "", enabled: true, menuId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userMenus", userId] });
        },
    });

    const removePermission = useMutation({
        mutationFn: (menuId: number) => deleteUserNavigateMenu({ userId: userId || "", menuId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userMenus", userId] });
        },
    });

    const changeAdmin = useMutation({
        mutationFn: ({ userId, admin }: { userId: string; admin: boolean }) => changeUserRol(userId, admin),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
    });

    const startTour = () => {
        const driverObj = driver({
            showProgress: true,
            showButtons: ['next', 'previous', 'close'],
            steps: [
                {
                    element: '#user-config-title',
                    popover: {
                        title: t('userConfiguration'),
                        description: t('tourUserConfigDesc') || 'Esta página te permite ver y gestionar toda la información de un usuario específico, incluyendo sus canales, planes activos y permisos de navegación.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#user-information-card',
                    popover: {
                        title: t('userInformation'),
                        description: t('tourUserInformationDesc') || 'Aquí puedes ver los datos básicos del usuario: nombre, correo electrónico, teléfono, ID único y rol en el sistema.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#user-role-toggle',
                    popover: {
                        title: t('changeRole'),
                        description: t('tourUserRoleDesc') || 'Haz click en este icono para cambiar el rol del usuario entre Administrador y Usuario normal.',
                        side: "left",
                        align: 'start'
                    }
                },
                {
                    element: '#user-channels-section',
                    popover: {
                        title: t('channels'),
                        description: t('tourUserChannelsDesc') || 'Esta sección muestra todos los canales de WhatsApp asociados al usuario, con su estado de autenticación y planes activos.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#channel-plans',
                    popover: {
                        title: t('activePlans'),
                        description: t('tourChannelPlansDesc') || 'Aquí se muestran todos los planes de suscripción activos del canal, con información sobre precio, diálogos disponibles, fechas y opciones de renovación.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#navigation-permissions',
                    popover: {
                        title: t('navigationPermissions'),
                        description: t('tourNavigationPermissionsDesc') || 'Gestiona los permisos de navegación específicos para este usuario. Puedes activar o desactivar menús individuales independientemente de la configuración global.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#permission-toggle',
                    popover: {
                        title: t('togglePermissions'),
                        description: t('tourPermissionToggleDesc') || 'Usa estos botones para activar o desactivar permisos específicos de navegación para este usuario.',
                        side: "left",
                        align: 'start'
                    }
                }
            ]
        });

        driverObj.drive();
    };

    // Filtrar el usuario actual
    const currentUser = useMemo(() => users?.find(u => u.id === userId), [users, userId]);

    const isLoading = usersLoading || channelsLoading || menusLoading || userMenusLoading;
    const error = usersError || channelsError;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-gray-600">{t('loadingUserInformation')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-red-500">{t('errorLoadingInformation')}</p>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">{t('userNotFound')}</p>
            </div>
        );
    }

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div id="user-config-title">
                        <h1 className="text-2xl font-bold text-gray-900">{t('userConfiguration')}</h1>
                        <p className="text-sm text-gray-600">{t('userInformationAndChannels')}</p>
                    </div>
                    <button
                        onClick={startTour}
                        className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100 hover:text-indigo-600"
                        title={t('startTour') || 'Iniciar tour'}
                    >
                        <HelpCircle className="h-6 w-6" />
                    </button>
                </div>
            </div>

            {/* Información del Usuario */}
            <div id="user-information-card" className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-900">{t('userInformation')}</h2>
                </div>

                <div className="px-6 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                        {/* Avatar */}
                        <div>
                            {currentUser.profileImageUrl ? (
                                <img
                                    src={currentUser.profileImageUrl}
                                    alt={currentUser.name || t('user')}
                                    className="h-32 w-32 rounded-full object-cover shadow-md"
                                />
                            ) : (
                                <div className="flex h-32 w-32 items-center justify-center rounded-full bg-indigo-100 shadow-md">
                                    <UserIcon className="h-16 w-16 text-indigo-600" />
                                </div>
                            )}
                        </div>

                        {/* Detalles */}
                        <div className="flex-1 space-y-4">
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('name')}</p>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {currentUser.name || t('noName')}
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <Mail className="h-5 w-5 text-gray-400" />
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('email')}</p>
                                    <p className="text-sm text-gray-900">{currentUser.email}</p>
                                </div>
                            </div>

                            {currentUser.phone && (
                                <div className="flex items-center gap-3">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('phone')}</p>
                                        <p className="text-sm text-gray-900">{currentUser.phone}</p>
                                    </div>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('userId')}</p>
                                <p className="mt-1 font-mono text-sm text-gray-600 break-all">{currentUser.id}</p>
                            </div>

                            <div>
                                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('role')}</p>
                                <p className="mt-1 " id="user-role-toggle">
                                    {currentUser.admin ? (
                                        <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                                            {t('administrator')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
                                            {t('user')}
                                        </span>
                                    )}

                                    <ArrowLeftRight className="inline-block h-4 w-4 ml-2 text-gray-500 hover:text-gray-700 hover:cursor-pointer" onClick={() => changeAdmin.mutate({ userId: userId || "", admin: !currentUser.admin })} />  
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Canales del Usuario */}
            <div id="user-channels-section" className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {t('channels')} ({channels?.length || 0})
                    </h2>
                </div>

                <div className="px-6 py-6">
                    {channels && channels.length > 0 ? (
                        <div className="space-y-6">
                            {channels.map((channel) => (
                                <div
                                    key={channel.id}
                                    className="rounded-lg border border-gray-200 bg-white p-4"
                                >
                                    {/* Información del Canal */}
                                    <div className="border-b border-gray-200 pb-4 mb-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {channel.nameChannel}
                                                </h3>
                                                <p className="text-xs text-gray-500">{t('channelId')}: {channel.id}</p>
                                            </div>
                                            {channel.whatsappSession && (
                                                <div className="flex items-center gap-2">
                                                    {channel.whatsappSession.isAuthenticated ? (
                                                        <>
                                                            <CheckCircle className="h-5 w-5 text-green-600" />
                                                            <span className="text-sm font-semibold text-green-700">{t('authenticated')}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircle className="h-5 w-5 text-yellow-600" />
                                                            <span className="text-sm font-semibold text-yellow-700">{t('notAuthenticated')}</span>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Detalles del Canal */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('phone')}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Smartphone className="h-4 w-4 text-gray-400" />
                                                <p className="text-sm text-gray-900">{channel.phoneChannel}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('creationDate')}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Calendar className="h-4 w-4 text-gray-400" />
                                                <p className="text-sm text-gray-900">
                                                    {new Date(channel.createdAt || channel.updatedAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Información de Sesión WhatsApp */}
                                    {channel.whatsappSession && (
                                        <div className="mb-4 rounded-lg bg-blue-50 p-3 border border-blue-200">
                                            <p className="text-xs font-medium uppercase tracking-wider text-blue-700 mb-2">{t('whatsappSession')}</p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                                                <div>
                                                    <p className="text-xs text-blue-600">{t('id')}</p>
                                                    <p className="font-mono text-xs">{channel.whatsappSession.id}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-blue-600">{t('created')}</p>
                                                    <p className="text-xs">{channel.whatsappSession?.createdAt && new Date(channel.whatsappSession.createdAt).toLocaleDateString()}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-blue-600">{t('updated')}</p>
                                                    <p className="text-xs">{channel.whatsappSession?.updatedAt && new Date(channel.whatsappSession.updatedAt).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Planes del Canal */}
                                    {channel.channelPlans && channel.channelPlans.length > 0 ? (
                                        <div id="channel-plans">
                                            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                                <TrendingUp className="h-4 w-4" />
                                                {t('activePlans')} ({channel.channelPlans.length})
                                            </h4>
                                            <div className="space-y-3">
                                                {channel.channelPlans.map((plan) => (
                                                    <div
                                                        key={plan.id}
                                                        className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                                                    >
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900">{plan?.plan?.name}</p>
                                                                <p className="text-xs text-gray-600">{plan?.plan?.description}</p>
                                                            </div>
                                                            <div>
                                                                {plan.active ? (
                                                                    <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                                                        {t('active')}
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                                                                        {t('inactive')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                                            <div>
                                                                <p className="text-gray-600">{t('price')}</p>
                                                                <p className="font-semibold text-gray-900">${plan?.plan?.price && (plan.plan.price / 100).toFixed(2)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-600">{t('dialogs')}</p>
                                                                <p className="font-semibold text-gray-900">{plan.usedDialogs}/{plan.dialogLimit || '∞'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-600">{t('start')}</p>
                                                                <p className="font-semibold text-gray-900 text-xs">
                                                                    {plan.startDate ? new Date(plan.startDate).toLocaleDateString() : 'N/A'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-600">{t('expiration')}</p>
                                                                <p className="font-semibold text-gray-900 text-xs">
                                                                    {plan.endDate ? new Date(plan.endDate).toLocaleDateString() : 'N/A'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                                            {plan.autoRenewal && (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-blue-700 font-medium">
                                                                    ✓ {t('autoRenewal')}
                                                                </span>
                                                            )}
                                                            {plan.manualPayment && (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-purple-700 font-medium">
                                                                    {t('manualPayment')}
                                                                </span>
                                                            )}
                                                        </div>

                                                        
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
                                            <p className="text-sm text-gray-500">{t('noPlansAssociatedWithChannel')}</p>
                                        </div>
                                    )}

                                    <IAUsage userId={userId || ""} channelId={channel.id} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-8">
                            <p className="text-gray-500">{t('userHasNoConfiguredChannels')}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Permisos de navegación del usuario */}
            <div id="navigation-permissions" className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h2 className="text-lg font-semibold text-gray-900">{t('navigationPermissions')}</h2>
                    <p className="text-sm text-gray-600">{t('activateOrDeactivateSpecificMenus')}</p>
                </div>

                <div className="px-6 py-6">
                    {!navigationMenus || navigationMenus.length === 0 ? (
                        <p className="text-gray-500">{t('noMenusAvailableForPermissions')}</p>
                    ) : (
                        <div className="space-y-3">
                            {navigationMenus.map((menu: NavigationMenuItem) => {
                                const isEnabled = userMenus?.some((um) => um.menuId === menu.id);
                                const isMutating = addPermission.isPending || removePermission.isPending;
                                let Icon = LayoutGrid;

                                switch (menu.icon) {
                                    case "MessageSquare":
                                        Icon = MessageSquare;
                                        break;
                                    case "Workflow":
                                        Icon = Workflow;
                                        break;
                                    case "MessageCircle":
                                        Icon = MessageCircle;
                                        break;
                                    case "Smartphone":
                                        Icon = Smartphone;
                                        break;
                                    case "EuroIcon":
                                        Icon = EuroIcon;
                                        break;
                                }

                                const handleToggle = () => {
                                    if (!userId) return;
                                    if (isEnabled) {
                                        removePermission.mutate(menu.id);
                                    } else {
                                        addPermission.mutate(menu.id);
                                    }
                                };

                                return (
                                    <div
                                        key={menu.id}
                                        className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-100">
                                                <Icon className="h-5 w-5 text-indigo-700" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-sm font-semibold text-gray-900">{menu.translationKey}</p>
                                                <div className="flex flex-wrap gap-2 text-xs">
                                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-semibold ${
                                                        menu.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {t('global')}: {menu.active ? t('active') : t('inactive')}
                                                    </span>
                                                    {menu.isSystem && (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 font-semibold text-blue-800">
                                                            {t('system')}
                                                        </span>
                                                    )}
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 font-semibold text-gray-700">
                                                        {t('order')}: {menu.order}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <button
                                                id="permission-toggle"
                                                onClick={handleToggle}
                                                disabled={isMutating}
                                                className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                                    !isEnabled ? "text-red-700 hover:text-red-800" : "text-green-700 hover:text-green-800"
                                                }`}
                                            >
                                                {!isEnabled ? (
                                                    <ToggleLeft className="h-6 w-6 text-red-700 hover:text-red-800" />
                                                ) : (
                                                    <ToggleRight className="h-6 w-6 text-green-700 hover:text-green-800" />
                                                )}
                                                {!isEnabled ? t('deactivated') : t('activated')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default UserConfig;